import React from 'react';
import styled from '@benzinga/themetron';
import { News } from '@benzinga/basic-news-manager';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { PostElapsed } from '@benzinga/news';
import Image from 'next/image';

interface Props {
  news: News;
}

export const NewsListItem = ({ news }: Props) => {
  if (!news) return null;

  return (
    <MoneyNewsBlockWrapper className="news-block-wrapper">
      {news?.image && (
        <a className="news-image-wrapper" href={news.url}>
          <Image
            alt={news?.title as string}
            className="news-image"
            height={124}
            sizes="(max-width: 567px) 100vw, 154px"
            src={news.image as string}
            width={154}
          />
        </a>
      )}
      <div className="news-content-wrapper">
        <a href={news?.url}>
          <h3 className="news-title">
            {news?.title && <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(news?.title) }} />}
          </h3>
        </a>
        {news?.teaser && <p className="news-description">{news?.teaser}</p>}
        <div className="news-statistics-wrapper">
          <div className="news-created-date">
            <PostElapsed created={news.created} />
          </div>
          <p className="news-author-name">{news?.author}</p>
        </div>
      </div>
    </MoneyNewsBlockWrapper>
  );
};

const MoneyNewsBlockWrapper = styled.div`
  &.news-block-wrapper {
    display: flex;
    align-items: stretch;
    gap: 24px;

    @media screen and (max-width: 567px) {
      flex-direction: column;
      gap: 16px;
    }
  }

  .news-image-wrapper {
    flex-shrink: 0;
  }

  .news-image {
    width: 154px;
    height: 124px;
    border-radius: 4px;
    object-fit: cover;

    @media screen and (max-width: 567px) {
      width: 100%;
      height: 100%;
    }
  }

  .news-content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .news-title {
    font-weight: 700;
    font-size: 20px;
    line-height: 28px;
    color: #283d59;
    cursor: pointer;
    transition: 0.25s color ease-in-out;

    &:hover {
      color: #3f83f8;
    }
  }

  .news-description {
    flex: 1;
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    color: #5b7292;
    display: -webkit-box;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;

    @media screen and (max-width: 567px) {
      -webkit-line-clamp: 3;
    }
  }

  .news-statistics-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .news-created-date {
    margin-right: 8px;
    font-weight: 700;
    font-size: 10px;
    line-height: 24px;
    text-transform: uppercase;
    color: #5b7292;
    border: 1px solid #e1ebfa;
    border-radius: 16px;
    padding: 0 16px;
  }

  .news-author-name {
    font-weight: 700;
    font-size: 14px;
    line-height: 24px;
    color: #3f83f8;
  }

  .news-separator {
    color: #ceddf2;
  }

  .news-author-role {
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    color: #5b7292;
  }
`;
